import React from 'react';
import { StateProvider } from 'polyphony/components/generic';
import { slugify } from 'polyphony/slug';
import { observeStore } from 'polyphony/redux';

/*
    Gutenberg Block Implementation

    Stores no ReactJS code inside WordPress. See implementation details.

    Design:

    Gutenberg Blocks get rendered in two different modes: "edit" and "save".
    1. For edit, we render the real React component, passing all inner blocks as JSX in props.children.
    2. For save, we render a div with information about the block we can use to mount the react component
    when the frontend is loaded.


    IDEAS:

    1. Allow blocks to only register on the backend & always pass children nodes via props. This would enable a scenario where:
         a. There's a specific parent block
         b. There are specific innerblocks that render div's with datasets & children
         c. When the parent block is mounted on the frontend, it will read the divs and configure itself.
*/

export const BLOCK_CLASS = "polyphony-gutenberg-react-block";

// Returns whether or not we're in the Gutenberg editor editing a post.
export const isGutenberg = () => window.wp && wp.editPost;

export function activateGutenberg() {
    if (isGutenberg()) {
        startBackendObserver();
    }
}

export const slugFor = Block => Block.slug ? Block.slug : Block.title ? slugify(Block.title) : '';

/*
    Gutenberg Editor
*/

const GenericEditor = ({Block, attributes, setAttributes, isSelected, _storageKey:sk, innerBlocks, ...gutenberg}) => {
    let props = attributes[sk] ? JSON.parse(attributes[sk]) : {};

    const { InnerBlocks } = wp.blockEditor;

    return <StateProvider><Block {...props} children={<InnerBlocks {...innerBlocks} />} gutenberg={{
        ...gutenberg,
        setProp: (k, v) => {
            setAttributes({ [sk]: JSON.stringify({...props, [k]: v}) });
        },
        isSelected,
        isEditor: true
    }} /></StateProvider>;
};

function blockToBlockname(Block) {
    if (typeof Block === 'string') return Block;
    return Block.blockName ? Block.blockName : 'polyphony/' + slugFor(Block);
}

function registerInEditor(Block) {
    // To allow for the configuration of React props,
    // we serialize a JSON dict of props and shove
    // it in a Gutenberg attribute.
    //
    // The editor will get an additional prop: `this.props.gutenberg`.
    // This contains a function setProp which updates the React component's
    // saved props, as well as other information about the state of the Block.

    const { InnerBlocks } = wp.blockEditor;
    const { registerBlockType, getBlockType, unregisterBlockType } = wp.blocks;

    let slug = slugFor(Block);
    let blockname = blockToBlockname(Block);

    if (getBlockType(blockname)) {
        unregisterBlockType(blockname);
    }

    let kws = Block.keywords;
    if (!kws || kws.length === 0) kws = slug.split('-').slice(0, 3);

    let as = {
        props_json: {
            default: '{}',
            type: 'string',
            source: 'attribute',
            selector: `div.${BLOCK_CLASS}`,
            attribute: 'data-props'
        }
    };

    let storageKey = 'props_json';

    if (Block.storage_key && Block.storage_key !== storageKey) {
        storageKey = Block.storage_key;
        as[storageKey] = {default: '{}', type: 'string'};
    }

    const {allowedBlocks: _allowedBlocks, template: _template, templateInsertUpdatesSelection, templateLock} = Block.innerBlocks ? Block.innerBlocks : {};

    const template = _template; // TODO: allow React components to appear here.
    const allowedBlocks = _allowedBlocks ? _allowedBlocks.map(blockToBlockname) : undefined;

    const deprecated = [];

    if (Block.deprecated) {
        for (const {reactMigrate, ...d} of Block.deprecated) {
            if (reactMigrate) {
                deprecated.push({...d, migrate(props) {
                    return {[storageKey]: JSON.stringify(reactMigrate(props))};
                }});
            } else {
                deprecated.push(d);
            }
        }
    }

    registerBlockType(blockname, {
        title: Block.title,
        description: Block.description,
        icon: Block.icon || 'wordpress-alt',
        category: Block.category || 'common',
        keywords: kws,
        supports: {
            html: false,
            align: false,
            customClassName: false,
            className: false
        },
        attributes: as,
        deprecated,
        transforms: Block.transforms,
        edit: props => <GenericEditor Block={Block} _storageKey={storageKey} {...props} innerBlocks={{allowedBlocks, template, templateLock, templateInsertUpdatesSelection}} />,
        save: ({ attributes }) => <div className={BLOCK_CLASS}
                                       data-block-slug={slug}
                                       data-props={attributes[storageKey]}><InnerBlocks.Content /></div>
    });
}

/*
    Observation
*/

function observeBlockSubscription(callback) {
    observeStore(polyphony.store, s => s.blocks, (prev, current) => {
        for (let k of Object.keys(current)) {
            if (!prev[k] || prev[k] !== current[k]) {
                callback(current[k]);
            }
        }
    });
}

function startBackendObserver() {
    observeBlockSubscription(registerInEditor);
}

